import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Config } from '../../common/config';
import useAxiosFetch from '../../hooks/useAxiosFetch';
import { getErrorMessage } from '../../components/utils';

/**
 * [2026-10-27] 룰렛 경품 구매 계정 (상점관리 — 내 상점 1개).
 *  백엔드: admin-promotion.controller.ts purchase-account · game/purchased-gifts · game/item/from-purchase ·
 *          game/item/:itemNo/attach-purchase · detach-purchase (간단정리 문서 7장, 운영자 · 채널과 같은 모양)
 *  연결: 1회용 코드 발급 → 마켓 /purchase-account/link?token= 팝업 → 소셜 로그인한 계정을 이 상점 구매 계정으로.
 */

const BASE = `${Config.API_SERVER}promotion`;
const PROVIDER_LABEL = { kakao: '카카오', naver: '네이버', google: '구글' };
const isOk = res => res && res.status >= 200 && res.status < 300;

function linkUrl(token) {
  const base = (Config.SHOPUSER_SERVER || 'https://shopuser-dev.naegift.com/').replace(/\/+$/, '');
  return `${base}/purchase-account/link?token=${encodeURIComponent(token)}`;
}

/** 경품 구매 계정 박스 — 연결 상태 · [연결] · [변경] · [해제] */
export function PurchaseAccountBox({ onChange }) {
  const { fetchData } = useAxiosFetch();
  const [account, setAccount] = useState(undefined); // undefined=로딩, null=미연결
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const res = await fetchData({ url: `${BASE}/purchase-account`, method: 'get' });
    if (!isOk(res)) {
      setAccount(null);
      setFailed(true);
      onChange?.(null);
      return;
    }
    const data = res?.data?.data;
    const next = data && data.userNo != null ? data : null;
    setAccount(next);
    setFailed(false);
    onChange?.(next);
  }, [fetchData, onChange]);

  useEffect(() => {
    load();
    const onMessage = e => {
      if (e?.data?.type === 'purchase-account-linked') load();
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [load]);

  const handleLink = async () => {
    // [2026-11-05] 실수 방지 — 새 창을 열기 전에 한 번 묻는다
    if (
      !window.confirm(
        account
          ? '구매 계정을 다른 계정으로 바꿀까요?\n새 창에서 경품을 살 마켓 계정으로 로그인하면 바뀌어요.'
          : '구매 계정을 연결할까요?\n새 창에서 경품을 살 마켓 계정으로 로그인해 주세요.'
      )
    )
      return;
    setBusy(true);
    const res = await fetchData({ url: `${BASE}/purchase-account/link-token`, method: 'post' });
    setBusy(false);
    const token = res?.data?.data?.token;
    if (!isOk(res) || !token) {
      alert(getErrorMessage(res) || '연결 코드 발급에 실패했습니다.');
      return;
    }
    const popup = window.open(linkUrl(token), 'purchaseAccountLink', 'width=480,height=720');
    if (!popup) {
      alert('팝업이 차단됐습니다. 브라우저에서 이 사이트의 팝업을 허용해 주세요.');
      return;
    }
    // postMessage 를 못 받는 경우 대비 — 창이 닫히면 다시 읽는다
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        load();
      }
    }, 800);
  };

  const handleUnlink = async () => {
    if (!window.confirm('구매 계정 연결을 해제할까요?\n이미 경품으로 연결한 기프트는 그대로 남습니다.')) return;
    setBusy(true);
    const res = await fetchData({ url: `${BASE}/purchase-account`, method: 'delete' });
    setBusy(false);
    if (!isOk(res)) {
      alert(getErrorMessage(res) || '해제에 실패했습니다.');
      return;
    }
    load();
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        border: '1px solid #ddd',
        borderRadius: 6,
        padding: '10px 12px',
        marginBottom: 16,
        fontSize: 13,
      }}
    >
      <strong>경품 구매 계정</strong>
      {account === undefined ? (
        <span style={{ color: '#888' }}>불러오는 중...</span>
      ) : account ? (
        <>
          <span style={{ color: '#2c6b45', fontWeight: 600 }}>연결됨</span>
          <span>
            {PROVIDER_LABEL[account.provider] || account.provider || ''} {account.email || `회원 #${account.userNo}`}
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button className="button_04 button_gray_line" onClick={handleLink} disabled={busy}>
              변경
            </button>
            <button className="button_04 button_gray_line" onClick={handleUnlink} disabled={busy}>
              해제
            </button>
          </span>
        </>
      ) : (
        <>
          <span style={{ color: '#888' }}>미연결</span>
          <span style={{ color: '#888', fontSize: 12 }}>
            {failed
              ? '구매 계정 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'
              : '경품을 살 마켓 계정을 연결하면, 그 계정이 산 기프트로 경품을 등록할 수 있어요. (PC에서 연결해 주세요)'}
          </span>
          <span style={{ marginLeft: 'auto' }}>
            <button className="button_04 button_red" onClick={handleLink} disabled={busy}>
              구매 계정 연결
            </button>
          </span>
        </>
      )}
    </div>
  );
}

/**
 * 구매 계정이 산 기프트 목록에서 경품 등록.
 *  target = { mode: 'create', roundSeq } | { mode: 'attach', item }
 */
export function PurchasedGiftPicker({ promotionNo, target, accountLinked, onClose, onDone }) {
  const { fetchData } = useAxiosFetch();
  const attach = target.mode === 'attach';
  const fixedGiftNo = attach ? target.item.giftNo : null;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGiftNo, setSelectedGiftNo] = useState(fixedGiftNo);
  const [count, setCount] = useState('1');
  const [itemName, setItemName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!accountLinked) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const qs = fixedGiftNo != null ? `?giftNo=${fixedGiftNo}` : '';
      const res = await fetchData({ url: `${BASE}/${promotionNo}/game/purchased-gifts${qs}`, method: 'get' });
      if (cancelled) return;
      if (!isOk(res)) {
        setRows([]);
        setError(getErrorMessage(res) || '구매한 기프트 목록을 불러오지 못했습니다.');
      } else {
        const data = res?.data?.data;
        const list = Array.isArray(data) ? data : data?.list ?? [];
        setRows(fixedGiftNo != null ? list.filter(r => r.giftNo === fixedGiftNo) : list);
        setError(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchData, promotionNo, fixedGiftNo, accountLinked]);

  const selected = useMemo(() => rows.find(r => r.giftNo === selectedGiftNo) || null, [rows, selectedGiftNo]);
  const max = selected?.remainCount ?? 0;

  const pick = row => {
    setSelectedGiftNo(row.giftNo);
    setCount('1');
    if (!attach) setItemName(row.giftName || '');
  };

  const handleSubmit = async () => {
    if (!selected) {
      alert('기프트를 선택하세요.');
      return;
    }
    const n = parseInt(count, 10);
    if (!Number.isFinite(n) || n < 1) {
      alert('개수는 1 이상이어야 합니다.');
      return;
    }
    if (n > max) {
      alert(`최대 ${max}개까지 넣을 수 있어요.`);
      return;
    }
    if (!attach && !itemName.trim()) {
      alert('상품명을 입력하세요.');
      return;
    }
    // [2026-11-05] 실수 방지 — 넣기 전에 한 번 묻는다
    const ask = attach
      ? `"${target.item.itemName}"에 기프트 ${n}개를 더 넣을까요?\n넣은 기프트는 구매 계정 목록에서 빠져요.`
      : `${target.roundSeq}회차에 "${itemName.trim()}" 경품 ${n}개를 넣을까요?\n넣은 기프트는 구매 계정 목록에서 빠져요.`;
    if (!window.confirm(ask)) return;
    setSaving(true);
    const res = await fetchData(
      attach
        ? {
            url: `${BASE}/game/item/${target.item.promotionGameItemNo}/attach-purchase`,
            method: 'post',
            data: { count: n },
          }
        : {
            url: `${BASE}/${promotionNo}/game/item/from-purchase`,
            method: 'post',
            data: { roundSeq: target.roundSeq, giftNo: selected.giftNo, count: n, itemName: itemName.trim() },
          }
    );
    setSaving(false);
    if (!isOk(res)) {
      alert(getErrorMessage(res) || '경품 등록에 실패했습니다.');
      return;
    }
    onDone();
  };

  const fmtDate = v => (v ? new Date(v).toLocaleDateString('ko-KR') : '');
  const th = { padding: 8, background: '#f6f7f9', fontSize: 13 };
  const td = { padding: 8, fontSize: 13, borderTop: '1px solid #eee' };

  return (
    <div
      role="dialog"
      aria-modal
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      // onClick={saving ? undefined : onClose}
      // [2026-11-05] 개수·이름 입력 팝업 — 바깥 클릭으로 닫히지 않게(입력값 유실 방지). [취소]로 닫는다
    >
      <div
        style={{ background: '#fff', borderRadius: 8, padding: 20, width: '100%', maxWidth: 720, maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
      >
        <h3 className="modal_popup_tit_01">
          {attach ? `"${target.item.itemName}" 개수 늘리기` : `${target.roundSeq}회차 — 경품 추가`}
        </h3>

        {!accountLinked ? (
          <p style={{ fontSize: 13, color: '#888', margin: '8px 0 16px' }}>
            경품을 살 계정이 아직 연결되지 않았어요. 화면 위 [구매 계정 연결]을 먼저 눌러 주세요.
          </p>
        ) : (
          <>
            <p style={{ fontSize: 12, color: '#888', lineHeight: 1.5, margin: '8px 0' }}>
              구매 계정으로 사 두고 아직 아무도 받지 않은 기프트만 보여요. 환불한 것, 유효기간이 룰렛 종료 후 30일보다 짧은 것은 빠져요. 경품용 기프트는 받기 링크를 보내지 마세요.
            </p>
            <div style={{ border: '1px solid #ddd', borderRadius: 4, maxHeight: 320, overflowY: 'auto', marginBottom: 12 }}>
              {loading ? (
                <p style={{ padding: 12, fontSize: 13 }}>불러오는 중...</p>
              ) : error ? (
                <p style={{ padding: 12, fontSize: 13, color: '#c00' }}>{error}</p>
              ) : rows.length === 0 ? (
                <p style={{ padding: 12, fontSize: 13, color: '#888' }}>
                  {attach
                    ? '더 넣을 수 있는 기프트가 없어요. 구매 계정으로 같은 기프트를 더 사 주세요.'
                    : '경품으로 넣을 수 있는 기프트가 없어요. 구매 계정으로 [지인에게 선물하기]로 사고 링크는 열지 마세요. 유효기간이 룰렛 종료일 + 30일 이후인 기프트만 나와요.'}
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, width: 40 }} />
                      <th style={{ ...th, textAlign: 'left' }}>기프트</th>
                      <th style={{ ...th, textAlign: 'right' }}>가격</th>
                      <th style={{ ...th, textAlign: 'right' }}>넣을 수 있는 개수</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr
                        key={r.giftNo}
                        onClick={() => pick(r)}
                        style={{ cursor: 'pointer', background: r.giftNo === selectedGiftNo ? '#fff3e8' : undefined }}
                      >
                        <td style={{ ...td, textAlign: 'center' }}>
                          <input type="radio" readOnly checked={r.giftNo === selectedGiftNo} />
                        </td>
                        <td style={td}>
                          {r.giftName}
                          {r.version != null && <span style={{ color: '#888', fontSize: 12 }}> (v{r.version})</span>}
                          {(r.firstPurchaseDttm || r.lastPurchaseDttm) && (
                            <span style={{ display: 'block', color: '#888', fontSize: 12 }}>
                              구매일 {fmtDate(r.firstPurchaseDttm)}
                              {r.lastPurchaseDttm && r.lastPurchaseDttm !== r.firstPurchaseDttm ? ` ~ ${fmtDate(r.lastPurchaseDttm)}` : ''}
                            </span>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>{Number(r.price || 0).toLocaleString()}원</td>
                        <td style={{ ...td, textAlign: 'right' }}>{r.remainCount ?? 0}개</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
              {!attach && (
                <input
                  className="input_01"
                  value={itemName}
                  onChange={e => setItemName(e.target.value)}
                  placeholder="상품명 (기본: 기프트명)"
                  style={{ flex: 1, minWidth: 160 }}
                  disabled={saving || !selected}
                />
              )}
              <input
                className="input_01_01"
                type="text"
                value={count}
                onChange={e => setCount(e.target.value.replace(/[^0-9]/g, ''))}
                style={{ width: 70 }}
                disabled={saving || !selected}
              />
              <span style={{ fontSize: 12, color: '#888' }}>개 {selected ? `(최대 ${max}개)` : ''}</span>
            </div>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="button_04 button_gray_line" onClick={onClose} disabled={saving}>
            취소
          </button>
          <button className="button_04 button_red" onClick={handleSubmit} disabled={saving || !accountLinked || !selected}>
            {saving ? '처리 중...' : attach ? '늘리기' : '경품 추가'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 경품 칸에서 구매분 N개 연결 해제 (배정 안 된 것만 — 서버가 판단) */
export function useDetachPurchase(onDone) {
  const { fetchData } = useAxiosFetch();
  return useCallback(
    async item => {
      const raw = window.prompt(`"${item.itemName}"에서 몇 개를 줄일까요?\n줄인 기프트는 구매 계정으로 돌아가서 환불하거나 다른 룰렛에 쓸 수 있어요.\n(이미 당첨된 것은 줄일 수 없어요)`, '1');
      if (raw == null) return;
      const n = parseInt(raw, 10);
      if (!Number.isFinite(n) || n < 1) {
        alert('개수는 1 이상이어야 합니다.');
        return;
      }
      const res = await fetchData({
        url: `${BASE}/game/item/${item.promotionGameItemNo}/detach-purchase`,
        method: 'post',
        data: { count: n },
      });
      if (!isOk(res)) {
        alert(getErrorMessage(res) || '개수를 줄이지 못했어요.');
        return;
      }
      onDone();
    },
    [fetchData, onDone]
  );
}
